import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import { Button } from '@localagent/ui/components/button';
import { Badge } from '@localagent/ui/components/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@localagent/ui/components/tabs';
import { toast } from '@localagent/ui/components/sonner';
import { uuidV4 } from '@localagent/sdk';
import { useArchiveWorkspace, useWorkspaceProjection } from '../hooks/useWorkspaces';
import { useAttachWorkspaceToChat } from '../hooks/useAttachWorkspaceToChat';
import {
  launchedWorkspaceIdsAtom,
  launchWorkspaceAction,
  dismissWorkspaceAction,
} from '../stores/workspace/atoms';
import { WorkspaceFilesPanel } from '../components/workspaces/WorkspaceFilesPanel';
import { WorkspaceIndexerStatus } from '../components/workspaces/WorkspaceIndexerStatus';
import { WorkspaceCloudDetailForProjection } from '../components/cloud-filesystem/ActiveWorkspaceCloudDetail';
import { WorkspaceStatePanelContent } from '../components/workspaces/WorkspaceStatePanel';
import { workspaceArchiveFailureMessage } from './workspaceArchiveFailure';
import { WorkspaceResourceSummary } from '../components/workspaces/WorkspaceResourceCards';
import { WorkspaceReadiness } from '../components/workspaces/WorkspaceReadiness';
import { WorkspaceContextTab } from '../components/workspaces/WorkspaceContextTab';
import { useExperienceMode } from '../hooks/useExperienceMode';
import { useCatalogSubscription } from '../hooks/useCatalogSubscription';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';

const WORKSPACE_DETAIL_TABS = ['files', 'overview', 'context'];

// Viewing is side-effect-free; launch, dismiss and archive are explicit acts (app-shell ADR-003).
export function WorkspaceDetailView() {
  useCatalogSubscription('context');
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const {
    projection: workspace,
    deviceId,
    isLoading,
    error,
    refreshRequested,
    revisionGap,
  } = useWorkspaceProjection(workspaceId ?? null);

  const launchedIds = useAtomValue(launchedWorkspaceIdsAtom);
  const launchWorkspace = useSetAtom(launchWorkspaceAction);
  const dismissWorkspace = useSetAtom(dismissWorkspaceAction);
  const archiveWorkspace = useArchiveWorkspace();
  const { attach, attaching } = useAttachWorkspaceToChat();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [archiveFailure, setArchiveFailure] = useState<string | null>(null);
  const { mode: experienceMode } = useExperienceMode();
  const showAdvancedChrome = experienceMode === 'advanced';
  const showNormalStatus = experienceMode === 'normal';

  useEffect(() => {
    if (workspace?.lifecycle === 'archived') {
      dismissWorkspace(workspace.workspace_id);
    }
  }, [dismissWorkspace, workspace]);

  const [searchParams, setSearchParams] = useSearchParams();
  const liveSearchParams = useRef(searchParams);
  liveSearchParams.current = searchParams;

  // URL state has exactly one writer, and it is never an effect (app-shell ADR-006).
  const writeUrl = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      const current = liveSearchParams.current;
      const next = new URLSearchParams(current);
      mutate(next);
      if (next.toString() === current.toString()) return;
      setSearchParams(next, { replace: true });
    },
    [setSearchParams]
  );

  const requestedTab = searchParams.get('tab');
  const activeTab =
    requestedTab && WORKSPACE_DETAIL_TABS.includes(requestedTab) ? requestedTab : 'files';

  const handleTabChange = useCallback(
    (tab: string) => {
      writeUrl((next) => {
        if (tab === 'files') next.delete('tab');
        else next.set('tab', tab);
      });
    },
    [writeUrl]
  );

  if (isLoading) {
    return (
      <LoadingState label="Loading workspace..." className="h-full" />
    );
  }

  // Explicit error state: unknown id or load failure gets a loud
  // block with a way back — no silent redirect, no empty render.
  if (error || !workspace) {
    return (
      <div className="flex h-full items-center justify-center" data-testid="workspace-detail-error">
        <div className="text-center">
          <p className="text-destructive">Failed to load workspace</p>
          {error && <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>}
          <Button
            variant="outline"
            className="mt-4"
            data-testid="workspace-detail-back"
            onClick={() => void navigate('/workspaces')}
          >
            Back to Workspaces
          </Button>
        </div>
      </div>
    );
  }

  const isLaunched = launchedIds.includes(workspace.workspace_id);
  const mode = workspace.assignment.routing_class;
  const workspacePath = workspace.binding?.folder_path ?? '';
  const archiveAvailability = workspace.actions.archive;

  return (
    <div className="flex h-full flex-col" data-testid="workspace-detail">
      <div className="border-b bg-background px-6 py-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            data-testid="workspace-detail-back"
            onClick={() => void navigate('/workspaces')}
          >
            &larr; Back
          </Button>
        </div>
        <div className="mt-3 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">{workspace.name}</h2>
              {mode === 'cloud' && <Badge variant="secondary">Cloud</Badge>}
              {mode === 'local' && <Badge variant="outline">Local</Badge>}
              {showAdvancedChrome ? <Badge variant="outline">{workspace.lifecycle}</Badge> : null}
              {showNormalStatus ? <WorkspaceReadiness projection={workspace} /> : null}
              {isLaunched && <Badge variant="success">Launched</Badge>}
            </div>
            {workspacePath && <p className="mt-1 text-sm text-muted-foreground">{workspacePath}</p>}
            {showAdvancedChrome ? (
              <WorkspaceResourceSummary projection={workspace} className="mt-2" />
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="workspace-detail-open-in-chat"
              disabled={attaching}
              onClick={() => attach(workspace.workspace_id)}
            >
              {attaching ? 'Opening…' : 'Open in chat'}
            </Button>
            {isLaunched ? (
              <Button
                variant="outline"
                data-testid="workspace-detail-dismiss"
                onClick={() => dismissWorkspace(workspace.workspace_id)}
              >
                Dismiss
              </Button>
            ) : (
              <Button
                variant="outline"
                data-testid="workspace-detail-launch"
                onClick={() => launchWorkspace(workspace.workspace_id)}
              >
                Launch
              </Button>
            )}
            {confirmingDelete && archiveAvailability.available ? (
              <>
                <Button variant="outline" size="sm" onClick={() => setConfirmingDelete(false)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={archiveWorkspace.isPending}
                  data-testid={`confirm-delete-${workspace.workspace_id}`}
                  onClick={() => {
                    setArchiveFailure(null);
                    archiveWorkspace.mutate(
                      { workspaceId: workspace.workspace_id, idempotencyKey: uuidV4() },
                      {
                        onSuccess: () => {
                          setArchiveFailure(null);
                          setConfirmingDelete(false);
                          handleTabChange('overview');
                          toast.success(`Archive started for workspace "${workspace.name}"`);
                        },
                        onError: (archiveError) => {
                          const message = workspaceArchiveFailureMessage(archiveError);
                          setArchiveFailure(message);
                          toast.error(message);
                        },
                      }
                    );
                  }}
                >
                  {archiveWorkspace.isPending ? 'Archiving...' : 'Confirm archive'}
                </Button>
              </>
            ) : (
              <Button
                variant="destructive"
                data-testid="workspace-detail-delete"
                disabled={!archiveAvailability.available}
                onClick={() => setConfirmingDelete(true)}
              >
                Archive
              </Button>
            )}
            {!archiveAvailability.available && (
              <div
                className="max-w-xs text-sm text-destructive"
                data-testid="workspace-detail-archive-refusal"
              >
                {archiveAvailability.refusal.message}
              </div>
            )}
            {archiveFailure && (
              <div
                className="max-w-xs text-sm text-destructive"
                data-testid="workspace-detail-archive-api-refusal"
              >
                {archiveFailure}
              </div>
            )}
          </div>
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="flex min-h-0 flex-1 flex-col"
      >
        <TabsList className="mx-6 mt-3 flex-shrink-0 self-start">
          <TabsTrigger value="files" data-testid="workspace-detail-tab-files">
            Files
          </TabsTrigger>
          <TabsTrigger value="overview" data-testid="workspace-detail-tab-overview">
            Overview
          </TabsTrigger>
          <TabsTrigger value="context" data-testid="workspace-detail-tab-context">
            Context
          </TabsTrigger>
        </TabsList>
        <TabsContent value="files" className="min-h-0 flex-1 pt-2">
          <WorkspaceFilesPanel
            workspaceId={workspace.workspace_id}
            workspacePath={workspacePath}
            mode={mode}
          />
        </TabsContent>
        <TabsContent value="overview" className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-4">
            {showAdvancedChrome ? (
              <WorkspaceStatePanelContent
                projection={workspace}
                deviceId={deviceId}
                isLoading={isLoading}
                error={error}
                refreshRequested={refreshRequested}
                revisionGap={revisionGap}
              />
            ) : null}
            {showAdvancedChrome ? (
              <WorkspaceIndexerStatus projection={workspace} workspacePath={workspacePath} />
            ) : null}
            {showAdvancedChrome ? (
              <WorkspaceCloudDetailForProjection projection={workspace} />
            ) : null}
          </div>
        </TabsContent>
        <TabsContent value="context" className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          <WorkspaceContextTab
            workspaceId={workspace.workspace_id}
            showAdvanced={showAdvancedChrome}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
